import React, { useLayoutEffect, useRef, useState } from 'react';
import { contrastInk, parseColor } from './contrastInk';

// What we assume when nothing in the ancestor chain paints a background: an
// unstyled page is white, so dark ink is the safe read.
export const FALLBACK_SURFACE = '#ffffff';

// Header themes set background-color on the <nav>, but the cart sits several
// levels below it, so we climb until we find the element that actually paints.
// Note: If an ancestor has a translucent background (0 < a < 1), it will be
// treated as if it were fully opaque; no alpha compositing is performed.
function resolveSurface(element: HTMLElement | null): string {
  let node: HTMLElement | null = element;

  while (node) {
    const { backgroundColor } = window.getComputedStyle(node);
    const parsed = parseColor(backgroundColor);
    if (parsed && parsed.a > 0) return backgroundColor;
    node = node.parentElement;
  }

  return FALLBACK_SURFACE;
}

// Measures the header the icon is sitting on and returns an ink guaranteed to
// contrast with it. Deliberately measured rather than looked up per studio: a
// per-studio table is exactly what drifted out of sync and produced ENG-2550,
// and this stays correct for studios that do not exist yet.
//
// Re-measures on resize/orientationchange because the painted ancestor can
// change out from under the icon without any React re-render or route
// change: navbar.scss flips `.navbar-collapse` between a white background
// below the $screen-md breakpoint and transparent above it, purely via a CSS
// media query. A mount below 768px followed by widening past it — e.g.
// rotating a phone to landscape — would otherwise leave the icon showing the
// ink it measured at mount, on a surface that no longer matches. `watch` is
// a separate, caller-driven escape hatch for forcing a re-measure (e.g. on
// navigation) and currently has no caller; it does not by itself cause
// re-measurement on viewport changes.
export default function useContrastingInk<T extends HTMLElement = HTMLElement>(
  watch?: unknown,
): {
  ref: React.RefObject<T>;
  surface: string;
  ink: string;
} {
  const ref = useRef<T>(null);
  const [surface, setSurface] = useState<string>(FALLBACK_SURFACE);

  // Layout effect so the first paint is already the right color.
  useLayoutEffect(() => {
    const measure = () => setSurface(resolveSurface(ref.current));
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('orientationchange', measure);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('orientationchange', measure);
    };
  }, [watch]);

  return { ref, surface, ink: contrastInk(surface) };
}
